import { generateAlphaPalette } from '../utils/alphaPalette';

const basePalette = {
  white: '#FFFFFF',
  black: '#000000',

  lavender: '#C5B4E3',
  lavender100: '#F5F2F9',
  lavender200: '#E1DAEC',
  lavender225: '#D8CBE2',
  lavender250: '#C6B4FF',
  lavender275: '#C5B6E3',
  lavender300: '#B8ABCE',
  lavender400: '#A394BD',
  lavender500: '#8E7FAB',
  lavender600: '#7A6B96',
  lavender700: '#65587F',
  lavender800: '#514668',
  lavender900: '#3E354F',

  stone: '#C8C9C7',
  stone200: '#F4F4F4',
  stone250: '#E5E5E5',
  stone300: '#C7C8C6',
  stone350: '#AEAEAE',
  stone400: '#8F8F8F',
  stone450: '#6E6E6E',

  gunmetal: '#333536',
  gunmetal200: '#EDEFF0',
  gunmetal250: '#E4E6E7',
  gunmetal300: '#D8DADB',
  gunmetal350: '#D0D2D3',
  gunmetal400: '#C4C6C7',
  gunmetal450: '#B7B9BA',
  gunmetal500: '#A9ABAC',
  gunmetal550: '#969899',
  gunmetal600: '#808080',
  gunmetal650: '#747677',
  gunmetal700: '#6B6D6E',
  gunmetal750: '#5B5D5E',
  gunmetal800: '#4E5051',
  gunmetal850: '#272E35',
  gunmetal900: '#1B1F24',

  gray150: '#F7F7F7',
  gray175: '#EEEEEE',
  gray200: '#DADBE3',
  gray250: '#EDEDF0',
  gray300: '#C2C4D1',
  gray400: '#8F92A6',
  gray500: '#70758C',

  marble: '#F5F5F5',

  red200: '#FFABAB',
  red500: '#C92C49',

  teal: '#5BE9DC',
  teal120: '#0B6060',

  green: '#0F8B8D',
  green200: '#E4F4F6',
  green500: '#1A7681',
  green700: '#004852',
  lime: '#00D64B',

  yellow: '#FFC043',

  blue: '#5897FB',

  purple: '#7F63B5',

  backgroundOverlay: 'rgba(250, 250, 250, 0.75)',

  // EqualWeb colors
  adaButtonMain: '#1C4BB6',

  // WARNING: colors below are not intended for SXF features but to not break components referencing them throughout the app. These should be deprecated in the future
  hydrowGreen: '#D1EF3D',
  hydrowGreen90: '#E4F495',

  outletRed: '#FE001A',

  deepBrown: '#332213',
  lightDeepBrown: '#5C4E42',
  yittyPurple: '#856387', // mamba in yitty colors

  modalOverlay: 'rgba(250, 250, 250, 0.75)',
};

/**
 * @description Generates an alpha palette for a given color and opacity percentages.
 * Colors will be defined by the colorPrefix with an appended opacity percentage.
 * This is used to differentiate between the base palette color and the alpha variations.
 *
 * @example
 * // const alphaPalette = {
 * //   ...generateAlphaPalette({
 * //     colorPrefix: 'gunmetal',
 * //     paletteColor: basePalette.gunmetal,
 * //     opacityPercentagesToGenerate: ['10', '20', '25', '35', '60'],
 * //   }),
 * //   ...generateAlphaPalette({
 * //     colorPrefix: 'lavender500',
 * //     paletteColor: basePalette.lavender500,
 * //     opacityPercentagesToGenerate: ['50'],
 * //   }),
 * // };
 *
 * Output:
 * // alphaPalette.gunmetal_10 // '#3335361A'
 * // alphaPalette.lavender500_50 // '#8E7FAB80'
 */
const alphaPalette = {
  ...generateAlphaPalette({
    colorPrefix: 'gunmetal',
    paletteColor: basePalette.gunmetal,
    opacityPercentagesToGenerate: ['10', '20', '25', '35', '50', '60'],
  }),
  ...generateAlphaPalette({
    colorPrefix: 'lavender500',
    paletteColor: basePalette.lavender500,
    opacityPercentagesToGenerate: ['50'],
  }),
  ...generateAlphaPalette({
    colorPrefix: 'lavender100',
    paletteColor: basePalette.lavender100,
    opacityPercentagesToGenerate: ['80'],
  }),
  ...generateAlphaPalette({
    colorPrefix: 'white',
    paletteColor: basePalette.white,
    opacityPercentagesToGenerate: ['50'],
  }),
  ...generateAlphaPalette({
    colorPrefix: 'stone',
    paletteColor: basePalette.stone,
    opacityPercentagesToGenerate: ['50'],
  }),
};

export const palette = {
  ...alphaPalette,
  ...basePalette,
};

export const colorTokens = {
  accountBoxContentBorder: palette.gunmetal300,
  accountBoxDivider: palette.gunmetal300,
  accountBoxWrapper: palette.gunmetal300,
  accountOrderDetailsBorder: palette.gunmetal300,
  accountOrderDetailsLineItem: palette.gunmetal800,
  mobileAccountPhoneNumberDisclaimerBorder: palette.gunmetal400,
  mobileAccountPhoneNumberDisclaimer: palette.gunmetal550,
  accountPhoneNumberDisclaimer: palette.gunmetal700,
  activateTrialSelectWrapper: palette.gunmetal400,
  arrowIcon: palette.black,
  backgroundTableRow: palette.gunmetal400,
  backgroundTableRowLight: palette.gunmetal250,
  backgroundCallout: palette.gunmetal650,
  backgroundCalloutAlternate: palette.gunmetal650,
  backgroundConfirmation: palette.white,
  backgroundDark: palette.gunmetal,
  backgroundAccounts: palette.gunmetal200,
  backgroundAccountsLight: palette.gunmetal200,
  backgroundVision: palette.gunmetal200,
  backgroundCheckout: palette.gunmetal200,
  backgroundDarkAlternate: palette.black,
  backgroundDefault: palette.black,
  backgroundDrawer: palette.gunmetal200,
  backgroundLight: palette.white,
  backgroundLightAlternate: palette.gunmetal400,
  backgroundDisabledLight: palette.gunmetal300,
  backgroundEmphasized: palette.black,
  backgroundEmphasizedLight: palette.gunmetal250,
  backgroundDeemphasizedLight: palette.gunmetal300,
  backgroundEmphasizedLightAlternate: palette.gunmetal300,
  backgroundOverlay: 'rgba(250, 250, 250, 0.75)',
  basketPricePromoClearance: palette.lavender700,
  billMeNowBorder: palette.gunmetal400,
  billMeNowConfAmount: palette.gunmetal400,
  bopsCancelBtn: palette.gunmetal,
  bopsChangeStoreBtn: palette.gunmetal,
  bopsHeaderBg: palette.gunmetal400,
  bopsListItemCounter: palette.gunmetal,
  borderDark: palette.black,
  border: palette.gunmetal400,
  borderLine: palette.gunmetal300,
  backgroundModalOverlay: 'rgba(0, 0, 0, 0.4)',
  borderLight: palette.gunmetal400,
  borderLightAlternate: palette.gunmetal550,
  borderShadow: palette.gunmetal350,
  builderAvailablePointsBorderInactive: palette.gunmetal400,
  builderAvailablePointsProgressCircle: palette.gunmetal400,
  builderBubbleImageWithLabelBorder: palette.gunmetal400,
  buttonPrimaryDisabledBg: palette.gunmetal350,
  buttonSecondaryDisabledBorder: palette.gunmetal650,
  buttonTertiaryDisabledFg: palette.gunmetal550,
  buttonTertiaryDisabledBg: palette.gunmetal350,
  buttonTertiaryBg: palette.gunmetal350,
  careGuideSectionBg: palette.gunmetal250,
  careGuideTitle: palette.black,
  ccPaymentFormWrapper: palette.gunmetal400,
  checkboxDisabled: palette.gunmetal250,
  compressionGuideSectionBg: palette.gunmetal250,
  compressionGuideOverlayButtonBg: palette.gunmetal250,
  compressionGuideOverlaySectionBg: palette.gunmetal250,
  compressionGuideOverlayCarouselBg: palette.gunmetal250,
  defaultRegFieldBorder: palette.gunmetal400,
  dropdownButtonBg: palette.gunmetal200,
  emphasizedNavItemLabel: palette.red500,
  empTooltipBg: palette.gunmetal400,
  enhancedNavTabBorder: palette.gunmetal,
  error: palette.red500,
  fabricCardBg: palette.gunmetal250,
  faqAccordionBg: palette.gunmetal400,
  findStoreBtn: palette.gunmetal,
  fitGuideBackground: palette.stone,
  fitGuideDefault: palette.gunmetal750,
  focusColor: palette.gunmetal650,
  ftvModalBackground: palette.white,
  genderTabBorder: palette.gunmetal,
  genericSelectorInputSize: palette.gunmetal400,
  genericSelectorInputSizeSoldOut: palette.gunmetal400,
  genericSelectorItemSoldOutBg: palette.gunmetal400,
  gridSwatchInputCheckedFocus: palette.blue,
  gridSwatchesLoadingSkeletonBorder: palette.gunmetal300,
  gridSwatchesLoadingSkeletonBg15: palette.gunmetal300,
  gridSwatchesLoadingSkeletonBg25: palette.gunmetal400,
  gridPrimarySwatchRadioButtonBorder: palette.gunmetal800,
  headNavFlyoutBackground: palette.white,
  headNavFlyoutBorder: palette.gunmetal300,
  headNavTabActive: palette.black,
  headNavTabInactive: palette.gunmetal,
  headNavTextDefault: palette.black,
  headNavTabActiveBackgroundMobile: palette.gunmetal,
  headNavTabActiveForegroundMobile: palette.white,
  headNavTabActiveBorderMobile: palette.gunmetal,
  headNavTabInactiveBackgroundMobile: palette.white,
  headNavTabInactiveForegroundMobile: palette.gunmetal,
  headNavTabInactiveBorderMobile: palette.gunmetal300,
  headNavTabContentBackgroundMobile: palette.white,
  headNavBorder: palette.gunmetal400,
  iconColor: palette.gunmetal,
  iconColorAlternate: palette.gunmetal650,
  iconColorCartCount: palette.lavender,
  iconColorDeemphasized: palette.gunmetal300,
  iconColorRemoveFromCart: palette.gunmetal,
  iconColorWishlist: palette.black,
  idMeVerifiedIcon: palette.gunmetal200,
  initiateReturnContainerBorder: palette.gunmetal400,
  learnMoreLink: palette.gunmetal,
  linkText: palette.gunmetal650,
  linkTextAlternate: palette.gunmetal650,
  linkTextDeemphasized: palette.gunmetal550,
  linkTextDeemphasizedAlternate: palette.gunmetal200,
  linkTextSpeedyTermsAndPrivacy: palette.lavender500,
  liveChatSurveyBorder: palette.gunmetal400,
  loyaltyActionCta: palette.lavender300,
  loyaltyActionCardBadge: palette.lavender300,
  loyaltyActionCardPoints: palette.lavender300,
  loyaltyActionCardBorder: palette.stone450,
  loyaltyHomepageActionCardBorder: palette.stone450,
  loyaltyLegacyBonusCardBorder: palette.stone450,
  loyaltyActivePrimaryButton: palette.lavender300,
  loyaltyLegacyActionCardBadge: palette.lavender300,
  loyaltyLegacyActionCardPoints: palette.lavender300,
  loyaltyRewardProgressPillBackground: palette.lavender300,
  loyaltyProgressTrackerPillBackground: palette.lavender300,
  loyaltyDisablePrimaryButton: palette.lavender100,
  loyaltyTierBadgeBg: '#1B1C19',
  loyaltyTierBadgeBorder: '#918E8C',
  loyaltyTierBadgeCompletedBorder: palette.gunmetal750,
  loyaltyVideoBannerSubtitle: palette.gunmetal750,
  loyaltyTooltipBg: palette.gunmetal400,
  loyaltyHomepageActionActionCardBorder: palette.stone,
  loyaltyHomepageActionActionCardCompletedBorder: palette.gunmetal750,
  loyaltyLegacyActionCardBg: palette.stone,
  loyaltyLegacyActionCardCompletedBg: palette.gunmetal750,
  loyaltyLegacyActionCardTitle: palette.gunmetal750,
  loyaltyRewardProgressTrackerBarBg: palette.stone,
  loyaltyRewardProgressTrackerBarCompletedBg: palette.gunmetal750,
  loyaltyActionVideoWrapperBackground: palette.gunmetal400,
  loyaltyProgressTrackerBarBg: palette.gunmetal400,
  loyaltyProgressTrackerBarCompletedBg: palette.gunmetal750,
  loyaltyProgressTrackerNotation: palette.gunmetal750,
  loyaltyActionCardBadgeBorder: palette.gunmetal750,
  loyaltyActionCardTitle: palette.gunmetal750,
  loyaltyActionCardSubtitle: palette.gunmetal750,
  loyaltyActionCardSelectedBackground: palette.gunmetal800,
  memberBoxBorder: palette.gunmetal400,
  memberCreditsM1Bg: palette.gunmetal250,
  memberInfoBackground: palette.stone200,
  memberInfoBorder: palette.stone300,
  memberInfoGreetingBorder: palette.gunmetal650,
  memberInfoLeadBackground: palette.stone200,
  memberInfoTrialDays: palette.gunmetal650,
  membershipBalanceWrapper: palette.gunmetal300,
  membershipBalanceAmountDivider: palette.gunmetal300,
  membershipBalanceTooltipBorder: palette.gunmetal300,
  membershipBalanceTooltipShadow: palette.gunmetal400,
  membershipContactChat: 'transparent',
  membershipCommunicationPreference: palette.gunmetal650,
  membershipContactChatText: palette.white,
  membershipContactPhone: palette.white,
  membershipContactPhoneText: palette.black,
  membershipContactTextActive: palette.black,
  membershipContactBoxBg: palette.gunmetal250,
  membershipOptInBorder: palette.gunmetal400,
  membershipOptInRadioButtonBlockSelectedBg: palette.stone,
  membershipTierElite: palette.lavender,
  membershipTierVIP: palette.gunmetal650,
  membershipFreeTrial: palette.lavender,
  metaNavBorder: '#EAEAEA',
  metaNavText: palette.gunmetal,
  metaNavListItemBg: palette.gunmetal,
  metaNavTextDeemphasized: palette.gunmetal550,
  metaNavResources: '#858583',
  mobileMetaNavListItemBorder: palette.stone300,
  metaNavListItemBorder: palette.gunmetal300,
  mobileMetaNavListItemBg: palette.gunmetal800,
  miniCartBorder: palette.gunmetal300,
  miniCartBundle: palette.gunmetal400,
  miniCartFocus: palette.gunmetal200,
  modialRelaySelectorClosestPointBorder: palette.gunmetal400,
  modialRelaySelectorClosestPointBg: palette.gunmetal400,
  modialRelayClosestPointBg: palette.stone,
  navMemberPanelStatusIsVip: palette.lavender300,
  navMemberPanelStatusNotVip: palette.gunmetal,
  offerButtonBg: palette.gunmetal,
  outfitDetailsLabel: palette.black,
  paymentOptionBorder: palette.gunmetal400,
  pdpBopsStoreFinderBg: palette.gunmetal400,
  pdpCalloutBgBrand: palette.purple,
  pdpCalloutYittyCollection: palette.yittyPurple,
  pdpCalloutBg: palette.black,
  pdpCalloutText: palette.white,
  pdpColorInputCheckedFocus: palette.blue,
  pdpColorSelectorInputBorder: palette.gunmetal400,
  pdpExtendedSizeCalloutBg: palette.gunmetal200,
  pdpOutfitSectionBg: palette.gunmetal200,
  pdpQuantitySelectorBorder: palette.gunmetal400,
  pdpShippingInfo: palette.gunmetal,
  philanthropyBorder: palette.gunmetal400,
  platinumExclusiveBackground: palette.gunmetal,
  postRegTime: palette.gunmetal400,
  postRegTimeWrapperBorder: palette.gunmetal400,
  preOrderCalloutBackground: palette.gunmetal,
  preorderRadioBorder: palette.gunmetal400,
  preorderRadioButtonBlockSelectedBg: palette.stone,
  promoText: palette.lavender,
  questionMarkToolTipShadow: palette.gunmetal400,
  radioButtonDisabled: palette.gunmetal250,
  returnInStoreBox: palette.gunmetal200,
  reviewBrowserSubmit: palette.gunmetal400,
  reviewsColorWrapper: palette.gunmetal200,
  reviewsDrawerFilterCount: palette.gunmetal400,
  saleText: palette.red500,
  memberOfferText: palette.purple,
  scrubsGroupOrdersLabel: palette.gunmetal400,
  scrubsGroupOrdersRadio: palette.gunmetal400,
  searchFieldWrapperBorder: palette.gunmetal300,
  searchPaneFieldContentColor: palette.gunmetal800,
  selectedFilterColor: palette.red500,
  selectBorder: palette.gunmetal400,
  quizAnswerBackgroundLight: palette.gray175,
  quizAnswerBackgroundDark: palette.gray175,
  quizAnswerBackgroundHover: palette.gray175,
  quizAnswerColorHover: palette.black,
  quizButtonBackground: palette.lavender275,
  quizButtonTextColor: palette.black,
  quizDropdownBorder: palette.gunmetal350,
  quizDropdownOptionBackground: palette.gunmetal250,
  quizEdit: palette.black,
  quizModalBackground: palette.white,
  quizSizingBorder: palette.gunmetal350,
  sizeSelectionSizingBorder: palette.gunmetal350,
  quizTextDark: palette.black,
  sizeSelectionDark: palette.black,
  quizTextLight: palette.gunmetal650,
  scarcityCallout: palette.lavender,
  separator: palette.gunmetal300,
  success: palette.green,
  searchInputClearBtn: palette.gunmetal250,
  searchInputField: palette.gunmetal250,
  sectionNodeFontColor: palette.gunmetal,
  shopNavPanelBorder: palette.gunmetal300,
  shopNavLinkBorder: palette.gunmetal300,
  skipToolTipBorder: palette.gunmetal300,
  skipToolTipShadow: palette.gunmetal400,
  mobileSkinnyBannerTermsButtonText: palette.white,
  sortDropdownBtnListBorder: palette.gunmetal300,
  sortDropdownListBorder: palette.gunmetal300,
  sortDropdownListItemBgHover: palette.stone250,
  speedySignupFormDropdownBg: palette.gunmetal250,
  stickyBannerBackground: palette.white,
  stickyCountdownTrackBar: palette.teal,
  stillHaveQuestionsFormWrapperBg: palette.gunmetal200,
  stillHaveQuestionsWrapperDisabledBg: palette.stone,
  storeLocationButtonWrapper: palette.gunmetal200,
  storeLocationDetailsWrapper: palette.gunmetal200,
  storeLocationRetailStoreHours: palette.gunmetal200,
  tabBorder: palette.black,
  tabBackground: palette.gunmetal400,
  tabSelected: palette.black,
  tabSelectedText: palette.white,
  tabDeselected: palette.white,
  tabDeselectedText: palette.black,
  taxShippingMessage: palette.gunmetal,
  textActionDeemphasized: palette.gunmetal,
  textConfirmation: palette.gunmetal650,
  textDeemphasized: palette.gunmetal550,
  textDeemphasizedAlternate: palette.gunmetal650,
  textDefault: palette.black,
  textFaded: palette.black,
  textEmphasized: palette.gunmetal,
  textEmphasizedAlternate: palette.gunmetal,
  textFadedAlternate: palette.gunmetal650,
  textHighlighted: palette.black,
  textHighlightedAlternate: palette.gunmetal,
  textLight: palette.white,
  textLightAlternate: palette.gunmetal650,
  textValue: palette.gunmetal650,
  thredUpBackground: '#eff0f2',
  toggleContainerBg: palette.gunmetal400,
  toggleEnabledBackground: palette.gunmetal300,
  toggleEnabledForeground: palette.gunmetal650,
  tooltipBorder: palette.gunmetal,
  topBarWrapperBorder: palette.gunmetal300,
  trialMembershipBoxBorder: palette.gunmetal400,
  referrerOptionDropdownFocus: palette.gunmetal350,
  ugcNodeWrapperBg: palette.gunmetal200,
  unsupportedMessageBg: palette.gunmetal,
  verifiedIconBg: palette.gunmetal,
  vipBadge: palette.gunmetal650,
  vipMembershipProgramWrapper: palette.teal120,
  warning: palette.yellow,
  styledHtmlAssetAnchor: palette.gunmetal600,
  inactivePaginator: palette.gunmetal600,
  accountPageMenuMobile: palette.gunmetal600,
  selectorText: palette.gunmetal600,
  returnConfirmationShipmentNote: palette.gunmetal600,
  initiateReturnOutfitLabel: palette.gunmetal600,
  leadMembershipAmount: palette.gunmetal600,
  idMeWhatIsAnchor: palette.gunmetal600,
  serviceDiscountTermsButton: palette.gunmetal600,
  navigationMoveLeftIcon: palette.gunmetal600,
  reviewsCount: palette.gunmetal600,
  productDetailCtaModalInfo: palette.gunmetal600,
  empAddToBagText: palette.gunmetal600,
  preorderCtaShippingInfo: palette.gunmetal600,
  productDetailProgressBar: palette.gunmetal600,
  progressBar: palette.gunmetal600,
  drawerReviewsFilters: palette.gunmetal600,
  drawerStyleResultCount: palette.gunmetal600,
  speedySignupReferrerDropdownLabel: palette.gunmetal600,
  empUpsellPrice: palette.gunmetal600,
  waitlistModalMessage: palette.gunmetal600,
  passwordFieldButton: palette.gunmetal600,
  upsellCarouselMessage: palette.gunmetal600,
  dropdownButton: palette.gunmetal600,
  dropdownListItem: palette.gunmetal600,
  compressionOverlayStickyHeader: palette.gunmetal600,
  compressionOverlayStickySubheader: palette.gunmetal600,
  fabricGuideHeader: palette.gunmetal600,
  womenFitGuideDisclosureText: palette.gunmetal600,
  checkboxInputBorder: palette.gunmetal600,
  radioButtonInputBorder: palette.gunmetal600,
  reviewsContentWrapper: palette.gunmetal300,
  offerSnoozeAcceptance: palette.gunmetal300,
  orderConfirmationCarousel: palette.gunmetal300,
  productListingFilter: palette.gunmetal300,
  outfitItemWrapper: palette.gunmetal300,
  outfitItemActions: palette.gunmetal300,
  outfitItemImage: palette.gunmetal300,
  productDetailSeparator: palette.gunmetal300,
  productDetailsOutfitMessage: palette.gunmetal300,
  productDetailsDrawerButtonBorder: palette.gunmetal300,
  mobileQuickViewHeader: palette.gunmetal300,
  mobileTopShadow: palette.gunmetal300,
  quickViewOutfitBorder: palette.gunmetal300,
  upsellCarouselBorder: palette.gunmetal300,
  wearItWithItemBorder: palette.gunmetal300,
  storeLocationDetailsSeparator: palette.gunmetal300,
  sizeGridTabBorder: palette.gunmetal300,
  womenFabricChartSeparator: palette.gunmetal300,
  careGuideSectionBorder: palette.gunmetal300,
  mobileHowItWorksMemberCredits: palette.gunmetal300,
  recommendedReviewBorder: palette.gunmetal300,
  recentReviewsBorder: palette.gunmetal300,
  recentReviewsWrapperBoxShadow: palette.gunmetal200,
  accountOrderDetails: palette.gunmetal300,
  accountOrderDetailsContentBorder: palette.gunmetal300,
  accountOrderDetailsShippingInfoBorder: palette.gunmetal300,
  addressConfirmationRadioButtons: palette.gunmetal300,
  addressSubFormLabelBorder: palette.gunmetal300,
  addressListBorder: palette.gunmetal300,
  basketItemBorder: palette.gunmetal300,
  cardSubformLabel: palette.gunmetal300,
  cardFormGridFieldsetBorder: palette.gunmetal300,
  ccpaConfirmBackground: palette.gunmetal300,
  ccpaConfirmationNumberBorder: palette.gunmetal300,
  initiateReturnConfirmationCardBorder: palette.gunmetal300,
  productSizeSelectorInputBorder: palette.gunmetal300,
  productSizeSelectorSoldOutBackground: palette.gunmetal300,
  initiateReturnRmaCardBorder: palette.gunmetal300,
  initiateReturnRmaCardSpacer: palette.gunmetal300,
  initiateReturnInstructionsPanelBorder: palette.gunmetal300,
  initiateReturnConfirmationBorder: palette.gunmetal300,
  initiateReturnConfirmationItemCardTopBorder: palette.gunmetal300,
  initiateReturnHappyReturnCardBorder: palette.gunmetal300,
  initiateReturnHappyReturnCardBorderSelected: palette.black,
  initiateReturnPrintLabelCardBorder: palette.gunmetal300,
  initiateReturnSelectBorder: palette.gunmetal300,
  initiateReturnCancelButton: palette.gunmetal300,
  initiateReturnBackground: palette.gunmetal300,
  leadMembershipInfoBorder: palette.gunmetal300,
  membershipCreditsTableBorder: palette.gunmetal300,
  membershipCreditsRowBorder: palette.gunmetal300,
  vipPerksCirle: palette.lavender300,
  myVipPerksBorder: palette.gunmetal300,
  myVipListBackground: palette.gunmetal300,
  myVipContactBorder: palette.gunmetal300,
  myVipContactQuestionsBorder: palette.gunmetal300,
  myVipContactVariantBorder: palette.gunmetal300,
  myVipContactVariantQuestionsBorder: palette.gunmetal300,
  carouselProgressIndicatorBackground: palette.gunmetal300,
  dropdownListBorder: palette.gunmetal300,
  dropdownButtonBorder: palette.gunmetal300,
  dropdownListItemBorder: palette.gunmetal300,
  compressionGuideBreakBorder: palette.gunmetal300,
  referrerOptionDropdownButtonBackground: palette.gunmetal300,
  speedySignupDropdownListBorder: palette.gunmetal300,
  closeButtonBorder: palette.gunmetal300,
  bottomNavPanelBorder: palette.gunmetal300,
  searchFieldBorder: palette.gunmetal300,
  flytHeadStickyBorder: palette.gunmetal300,
  headNavMiniCartArrowBorder: palette.gunmetal300,
  headNavAccountMenuBorder: palette.gunmetal300,
  orderSummaryTotalsContent: palette.gunmetal300,
  orderSummaryTotalsBorder: palette.gunmetal300,
  orderSummaryBorder: palette.gunmetal300,
  orderSummaryInfoLabelBorder: palette.gunmetal800,
  orderSummaryDetailItemLabel: palette.gunmetal800,
  paymentMethodListItem: palette.gunmetal300,
  pointsHistoryTableBorder: palette.gunmetal300,
  pointsHistoryTableRowBorder: palette.gunmetal300,
  profileFormBorder: palette.gunmetal300,
  perksListBorder: palette.gunmetal300,
  sizePreferencesListItemBorder: palette.gunmetal300,
  swatchInputBorder: palette.gunmetal300,
  enhancedNavButtonGroupBorder: palette.gunmetal300,
  enhancedNavListBorder: palette.gunmetal300,

  howItWorksBackground: palette.gunmetal450,
  howItWorksMemberCredits: palette.gunmetal450,
  howItWorksBenefitsBackground: palette.gunmetal450,
  howItWorksFeaturedGridBackground: palette.gunmetal450,
  howItWorksFaqBackground: palette.gunmetal450,
  loginAssetBorder: palette.gunmetal450,
  storeLocationsLocatorBackground: palette.gunmetal450,
  noResultsHorizontalRuleBorder: palette.gunmetal450,
  basketItemImageBackground: palette.gunmetal450,
  basketItemProductImageBackground: palette.gunmetal450,
  billMeNowPaymentFormBackground: palette.gunmetal450,
  billMeNowChangePaymentBackground: palette.gunmetal450,
  billMeNowDialogBackground: palette.gunmetal450,
  returnConfirmationBackground: palette.gunmetal450,
  returnConfirmationBtnBackground: palette.gunmetal650,
  initiateReturnInStoreBorder: palette.gunmetal450,
  fitAppMindfulnessBackground: palette.gunmetal450,
  faqAccordionButton: palette.gunmetal450,
  marketingCarouselBackground: palette.gunmetal450,
  searchFieldBackground: palette.stone200,
  searchSuggestIsHighlightedBackground: palette.stone250,
  scrubsSizeMessageBorder: palette.gunmetal450,
  quizDropdownOptionBackgroundMobile: palette.gunmetal450,
  quizDropdownParentHoverColor: palette.black,
  embroideryProgressBarBackground: palette.gunmetal450,
  embroideryTextSelectionPreviewContainerBg: palette.gunmetal400,
  memberBoxBottomInfoBackground: palette.gunmetal450,
  haveQuestionsCancelBorder: palette.gunmetal450,
  reviewBrowserSubmitDisabled: palette.gunmetal450,
  skipTheMonthBorder: palette.gunmetal450,
  skipTheMonthProfileHeaderBg: palette.stone,
  skipTheMonthHeadingWrapperBg: palette.stone200,
  skipTheMonthKhloeProfileHeaderBg: palette.stone200,
  skipTheMonthKhloeCtaBg: palette.stone200,
  postregSelectorDivider: palette.gunmetal450,
  postregOfferBorder: palette.gunmetal450,
  checkoutHeaderBorder: palette.gunmetal450,
  favoritesPlaceholderImageBackground: palette.gunmetal450,
  styleGuideAssetWrapper: palette.gunmetal450,
  enhancedNavMenuScrollbarBackground: palette.gunmetal450,
  aboutLayoutCollectionsBackground: palette.gunmetal450,
  disabledDropdownButtonBackground: palette.gunmetal450,
  dropdownListItemBackground: palette.gunmetal450,
  inputCheckedColor: palette.gunmetal450,
  sortActiveSelection: palette.black,
  searchSubmitButtonText: palette.gunmetal,
  returnInStoreViewMore: palette.gunmetal650,
  skipTheMonthConfSubHeader: palette.gunmetal650,
  activateTrialSelectBorder: palette.gunmetal650,
  footerSkinnyBannerWrapper: palette.gunmetal650,
  mobileSearchPanePlaceholder: palette.gunmetal650,
  pdpTooltipBorder: palette.gunmetal650,
  pdpTokenOnlyCallout: palette.gunmetal650,
  memberCreditCalloutToolipBorder: palette.gunmetal650,
  storeHoursInactive: palette.gunmetal650,
  fieldsetSelectBorder: palette.gunmetal650,
  fitAppPageBannerBg: palette.gunmetal650,
  storeLocationsDropdownArrow: palette.gunmetal650,
  storeLocationsDropdownLabel: palette.gunmetal650,

  orderConfirmationCarouselPromoPrice: palette.red500,
  loyaltyPrice: palette.red500,
  unavailableStockStatus: palette.red500,

  searchDivider: palette.gunmetal300,
  unfilledCompressionScaleBar: palette.gunmetal300,

  billMeNowSuccessOne: palette.gunmetal,
  loadingScreenBackground: palette.stone,
  errorPageBodyColor: palette.gunmetal,
  errorPageCopyHeadingColor: palette.gunmetal,
  errorPageFooterColor: palette.gunmetal,

  addPaymentIcon: palette.gunmetal550,
  placeholderImageBackground: palette.gunmetal550,
  soldOutSelectorItemSizeInput: palette.gunmetal550,
  soldOutSelectorItemSizeLabel: palette.gunmetal550,

  builderComparisonTableRowBackground: palette.gunmetal250,
  builderComparisonStyledDropdownBackground: palette.gunmetal250,
  storeLocationsMobileRegionLabel: palette.gunmetal250,

  headNavIcon: palette.gunmetal750,
  forgotPasswordControl: palette.gunmetal750,
  compressionGuideInfo: palette.gunmetal750,

  orderHistoryDetailsLink: palette.black,
  returnMessage: palette.black,
  ordersDataValue: palette.black,
  orderHistoryItemDetails: palette.black,
  onlineCancelFinalHeader: palette.black,
  onlineCancelFinalButton: palette.black,
  loyaltyLockIcon: palette.black,
  loyaltyActionCardIconColor: palette.gunmetal,
  loyaltyActionIconColor: palette.gunmetal,
  loyaltyActionProgressHeading: palette.gunmetal,
  loyaltyDeprecatedHomepageRewardsProgressHeading: palette.gunmetal,
  loyaltyDeprecatedRewardsProgressHeading: palette.gunmetal,
  loyaltyVideoBannerTitle: palette.gunmetal,
  loyaltyActionProgressCollapsibleIcon: palette.gunmetal600,
  rewardPromoCardTitle: palette.black,
  afterpayPlacement: palette.black,
  sizeChartBackgroundImage: palette.black,
  fitAppTextCopy: palette.black,
  sizeGridSizeTab: palette.black,
  defaultButtonOnHover: palette.black,

  backButton: palette.gunmetal600,
  liveChatCharactersRemaining: palette.gunmetal600,
  memberBoxBottomInfoClose: palette.gunmetal600,
  myVipContactCancelContactMethod: palette.gunmetal600,
  myVipContactAnytime: palette.gunmetal600,
  inputClearButton: palette.gunmetal600,
  inputShadow: palette.gunmetal600,
  speedySignupTermsWrapper: palette.gunmetal600,
  fitGuideLink: palette.gunmetal,
  fitGuideExclamationIcon: palette.gunmetal600,
  fabricCardAssetPlaceholder: palette.gunmetal600,
  hydrowTestimonialHeading: palette.gunmetal600,
  hydrowTestimonialText: palette.gunmetal600,
  inputSizeSelectorSoldOut: palette.gunmetal600,
  swatchTagLabel: palette.gunmetal700,

  desktopProductListingFilter: palette.gunmetal300,
  swatchesMoreColorsLink: palette.gunmetal400,
  inactiveTextInput: palette.gunmetal550,
  flickityPageDots: palette.gunmetal550,
  quizDropdownLabel: palette.gunmetal550,
  quizDropdownOptionBg: palette.stone200,
  hdyhQuizDropdownNativeSelectBorder: palette.gunmetal400,
  birthdaySpan: palette.gunmetal550,
  zipCodeInputColor: palette.gunmetal550,
  registrationFieldsLabel: palette.gunmetal550,
  disabledSecondaryButton: palette.gunmetal550,
  textDisabled: palette.gunmetal600,
  priceOfferOr: palette.gunmetal700,

  alertRed: palette.lavender,
  unfocusedBackground: palette.gunmetal450,
  sublabelBlack: palette.gunmetal750,
  focusText: palette.gunmetal800,
  focusBackground: palette.gunmetal,
  focusBorder: palette.gunmetal350,
  shippedSuccessLabel: palette.lavender,
  unfocusedLabel: palette.gunmetal550,
  unfocusedSublabel: palette.gunmetal550,
  unfocusedAction: palette.gunmetal600,
  unfocusedBorder: palette.gunmetal400,
  demureBorder: palette.gunmetal300,
  mobileSkinnyBannerTermsButton: palette.gunmetal,
  membershipStatusBadge: palette.gunmetal,
  wearItWithTermsButton: palette.gunmetal,
  membershipBalancesTagValue: palette.gunmetal,
  productReviewCount: palette.gunmetal600,
  addressListButtonDisabled: palette.gunmetal600,
  promoPriceStrikethrough: palette.gunmetal600,
  activeBreadcrumb: palette.gunmetal900,
  accountOrdersDataLabel: palette.gunmetal800,
  accountOrdersDataValue: palette.gunmetal800,
  doNotCancelVipLink: palette.black,
  dropdownMenuButton: palette.stone,
  dropdownMenuButtonBorder: palette.gunmetal400,
  canScrollGradient: palette.black,

  actionDisabled: palette.gunmetal650,
  actionEnabled: palette.black,
  fadedBackground: palette.stone,
  registrationPasswordField: palette.gunmetal750,
  passwordFieldFieldsetLabel: palette.gunmetal750,
  textFieldColor: palette.gunmetal750,
  textFieldFieldsetLabel: palette.gunmetal750,
  tableData: palette.gunmetal750,
  closeIcon: palette.gunmetal750,
  inactiveItem: palette.gunmetal650,
  navMemberSkip: palette.gunmetal550,
  closeButton: palette.gunmetal,
  loadingLineMessage: palette.gunmetal550,
  questionMarkTooltipAnchor: palette.gunmetal550,
  deselectFilterButton: palette.gunmetal550,
  builderSubtitle: palette.gunmetal550,
  shopNavBottomPanel: palette.gunmetal550,
  errorMessage: palette.red500,
  alertWarning: palette.yellow,
  returnRmaCartTitle: palette.lavender700,
  myVipContactQuestions: palette.stone,
  additionalText: palette.gunmetal700,
  faintBorder: palette.gunmetal300,
  faintBackground: palette.gunmetal300,

  filterPillUnselected: palette.stone,
  shoppableVideoProgressBackground: palette.gunmetal750,
  shoppableVideoDarkGray: palette.gunmetal650,
  shoppableVideoLightGray: palette.gunmetal550,
  shoppableVideoSilver: palette.gunmetal400,
  shoppableVideoFeaturedText: palette.gunmetal800,
  shoppableVideoFeaturedButton: palette.gunmetal,
  shoppableVideoLoaderBg: palette.gunmetal300,
  shoppableVideoCarouselTitle: palette.gunmetal,
  shoppableVideoTooltipBg: palette.lavender300,
  shoppableVideoSkeletonGradient20: '#9C9C9C33',
  shoppableVideoSkeletonGradient30: '#9C9C9C4D',
  shoppableVideoProductInfoOverlay: '#FFFFFFE6',
  shoppableVideoShadowShim0: '#00000000',
  shoppableVideoShadowShim16: '#0000002A',
  shoppableVideoShadowShim70: '#000000B3',
  shoppableVideoShadowShim90: '#000000E5',
  shoppableVideoProductCardOverlay: '#FFFFFF80',
  shoppableVideoCarouselDescription: palette.gunmetal,
  shoppableVideoCarouselFullWidthWrapperBg: palette.gunmetal250,
  shoppableVideoAuthorProfileImageBg: palette.gunmetal400,
  shoppableVideoIntroVideoTitle: palette.gunmetal800,
  shoppableVideoIntroVideoDescription: palette.gunmetal800,

  bouncebackEndowmentMiniCartPrompt: palette.lavender,
  bouncebackEndowmentHighlight: palette.lavender,

  promoPickerModalBackgroundTop: palette.lavender,
  promoPickerModalBackgroundBottom: palette.lavender500,
  promoPickerGiftBoxColor: palette.lavender,
  signupFormSmsDisclosure: palette.gunmetal650,
  promoPickerFormPanelsLink: palette.white,
  promoPickerTemplateSubheading: palette.white,
  promoPickerSignupFormSmsDisclosure: palette.white,
  promoPickerSignInPublicMessage: palette.white,
  promoPickerSignInSubheading: palette.white,
  promoPickerSignInDontHaveAccount: palette.white,
  promoPickerPrivacyConsent: palette.white,

  outletSplashSubmitButtonFocus: palette.lavender700,
  outletSplashPrimaryColor: palette.lavender,
  outletDisabledSplashSubmitButtonBg: palette.stone450,

  errorPageBackgroundColor: palette.lavender100,

  tieredListDropdownParentOptionBg: palette.stone200,

  searchCioSkeletonGradient15: palette.gunmetal300,
  searchCioSkeletonGradient25: palette.gunmetal400,
  searchCioBarLayoutBorder: palette.gunmetal350,
  searchCioOverlayNoResultsFoundTitle: palette.gunmetal700,
  searchCioDesktopInputPlaceholder: palette.gunmetal800,

  hamburgerMenuHeaderBannerBorder: palette.gunmetal300,

  pinVerificationLoginSectionHoverBg: palette.gunmetal350,
  pinVerificationFormDigitInputBorder: palette.gunmetal400,
  pinVerificationFormResendCodeText: palette.gunmetal550,
  pinVerificationLoginSectionBorder: palette.gunmetal400,
  pinVerificationMethodIcon: palette.gunmetal550,

  countryDropdownMenuBorder: palette.stone400,

  sendLinkToPhoneNumberInputBorder: palette.gunmetal400,
  sendLinkToPhoneNumberInputPlaceholder: palette.gunmetal800,

  sustainabilityCardWrapperBorder: palette.gunmetal400,

  loginAuthForgotPasswordControlButton: palette.gunmetal500,

  gridProductCardSwatchBorderDefault: palette.trueBlack,
  gridProductCardSwatchBorderSelected: palette.gunmetal800,
  gridProductCardSwatchBackgroundSelected: palette.white,
  gridProductCardSwatchMoreSwatchesIcon: palette.gunmetal400,
  gridSelectedFilterBackgroundColor: palette.gunmetal800,
  gridSelectedFiltersPillBackgroundColor: palette.stone200,
  gridSelectedFiltersPillIconColor: palette.gunmetal550,
  gridFacetListBorderColor: palette.gray175,
  gridBreadcrumbTextColor: palette.gunmetal650,
  gridActiveBreadcrumbTextColor: palette.gunmetal,
  gridCategoryTreeTextColor: palette.gunmetal900,
  gridCategoryTreeActiveBackgroundColor: '#eff0f0',
  builderCategoryNavLinkStyledListItem: palette.gunmetal900,
  builderCategoryNavLinkLabel: '#eff0f0',
  gridSortDropdownSortButtonBorderColor: palette.gunmetal300,
  gridSortDropdownItemBackgroundColorHover: palette.stone250,
  gridSortDropdownItemTextColorDefault: palette.gunmetal800,
  gridFilterGroupDisclosurePanelWrapperSelectedCountTextColor:
    palette.gunmetal650,
  gridDiscoveryGuideAssetCardInactiveBorderColor: palette.gunmetal450,

  memberBoxBoxShadow: palette.gunmetal200,
  klarnaCreditPromotion: palette.gunmetal600,

  semanticColors: {
    active: palette.lavender500,
    darkShadow1: palette.gunmetal_35,
    default: palette.black,
    focusShadow: palette.lavender500_50,
    lightShadow1: palette.gunmetal_10,
    lightShadow2: palette.gunmetal_20,
    lightShadow3: palette.gunmetal_25,
    scrim: palette.gunmetal_60,
    scrimLavender: palette.lavender100_80,
    promo: palette.lavender,
    subdued: palette.gunmetal600,
    whiteSubdued: palette.white_50,
  },
};
